import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { IconType } from '../../models/settings.model';
import { environment } from '../../../environments/environment';
import { AnalyticsService } from '../../analytics/analytics.service';
import { AuthorizationFacade } from '../../facades/authorization.facade';
import { FeaturedProductsFacade } from '../../facades/featured-products.facade';
import { MarketingFacade } from '../../facades/marketing.facade';
import { WishlistFacade } from '../../facades/wishlist.facade';
import { IProductImage, IProductLabel } from '../../models/abstract-product.models';
import {
  IFeatureProductPrices,
  IFeatureProducts,
  IFeatureProductsData,
  IFeatureProductWithImage,
} from '../../models/featured-products.models';
import { ISystemDetails } from '../../models/common.models';
import { FeaturedProductsUtils } from '../../utils/featuredProducts.utils';
import { ICart } from '../../models/cart.models';
import {
  CONFIG_PRICING_GROUP_FOR_CONSUMABLES,
  EFeatureToggles,
  EInstalledBaseTabs,
  EStoreFeatures,
  EStoreTypes,
} from '../../configurations/common';
import { AppUtils } from '../../utils/app.utils';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { PriceUtils } from '../../utils/price.utils';
import { CatalogFacade } from '../../facades/catalog.facade';
import { IInstallBaseQueryParams } from '../../models/catalog.models';
import { CustomerFacade } from '../../facades/customer.facade';
import { CartUtils } from '../../utils/cart.utils';
import { IBaseWishlist } from '../../models/wishlist.models';

@Component({
  selector: 'app-featured-product',
  templateUrl: './featured-product.component.html',
  styleUrls: ['./featured-product.component.scss'],
  standalone: false,
})
export class FeaturedProductComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private featuredProductsFacade: FeaturedProductsFacade = inject(FeaturedProductsFacade);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private marketingFacade: MarketingFacade = inject(MarketingFacade);
  private wishlistFacade: WishlistFacade = inject(WishlistFacade);
  private authorizationFacade: AuthorizationFacade = inject(AuthorizationFacade);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private catalogFacade: CatalogFacade = inject(CatalogFacade);
  private customerFacade: CustomerFacade = inject(CustomerFacade);

  iconType: typeof IconType = IconType;
  isUsStore: boolean = false;
  isCaStore: boolean = false;
  itemsInQueueToCart: string[] = [];
  loadingCartDataInProgress: boolean = true;
  featuredProductsWithPricesAndImages!: IFeatureProductWithImage[];
  isLoaded: boolean = false;
  hasProducts: boolean = false;
  currentLayoutType: string = 'grid';
  defaultImg: string = environment.defaultImg;
  slideConfigHorizontal: object = {};
  slideConfigVertical: object = {};
  wishlists: IBaseWishlist[] = [];
  isWishlistsActive: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.WISHLISTS);
  addNewModalActive: boolean = false;
  addItemToCartModalActive: boolean = false;
  showModalSelectEquipment: boolean = false;
  labels!: IProductLabel[];
  currentCart!: ICart;
  currentCartId!: string;
  hasCartContract: boolean = false;
  itemToAdd: any;
  isSapIntegration: boolean = false;
  isSapIntegrationRequired: boolean = false;
  priceUtils: typeof PriceUtils = PriceUtils;
  pricingGroupForConsumables!: string;
  isBusinessPartner: boolean = false;
  pricingGroupToggle: boolean = false;
  productPricingGroup!: string;
  isMyInstalledBaseFlowEnabled: boolean = false;
  isNonEquipmentPurchaseEnabled: boolean = false;
  isPredefinedEquipmentFlowEnabled: boolean = false;
  supportedCategoriesForNonEquipmentPurchase: string[] = [];

  @Input() featuredProductsKey!: string;

  private unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.isCaStore = AppUtils.isStoreActive(EStoreTypes.CA);
    this._selectIsPredefinedEquipmentFlowEnabled();
    this._isNonEquipmentPurchaseEnabled();

    if (this.isUsStore) {
      this.selectHasCartContract();
    }

    this.selectIsMyInstalledBaseFlowEnabled();
    this.selectCurrentCart();
    this.selectLoadingCartDataInProgress();
    this.selectFeaturedProducts();
    this.selectItemsInQueueToCart();
    this.selectPricingGroupToggle();
    this.selectIsCustomerBusinessPartner();

    this.configurationFacade.getSapStatus().subscribe(({sapIntegration, sapIntegrationRequired}) => {
      this.isSapIntegration = sapIntegration;
      this.isSapIntegrationRequired = sapIntegrationRequired;
    });

    this.authorizationFacade.selectIsUserLoggedIn().pipe(takeUntil(this.unsubscribe$)).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.dispatchActionToGetWishlists();
        this.getWishlistsFromStore();
      }
    });

    this.selectPricingGroupForConsumables();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private selectPricingGroupForConsumables(): void {
    this.catalogFacade.getPricingGroupForConsumables().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(pricingGroup => this.pricingGroupForConsumables = pricingGroup);
  }

  selectEquipment(): void {
    let commands: string = '/my-installed-base';
    let tab: EInstalledBaseTabs | null = EInstalledBaseTabs.EQUIPMENT;

    if (this.isBusinessPartner) {
      commands = '/equipment-selection';
      tab = null;
    }

    let queryParams: IInstallBaseQueryParams = {
      tab: tab,
    };

    if (this.pricingGroupToggle) {
      queryParams = {
        ...queryParams,
        pricing_group: this.productPricingGroup,
      };
    }
    this.router.navigate([commands], {
      queryParams: queryParams,
    }).then();
  }

  selectCurrentCart(): void {
    this.marketingFacade.selectCart().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cart => {
      if (cart) {
        this.currentCart = cart;
        this.currentCartId = cart.id;
      }
    });
  }

  selectLoadingCartDataInProgress(): void {
    this.marketingFacade.selectLoadingCartDataInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(loadingCartDataInProgress => this.loadingCartDataInProgress = loadingCartDataInProgress);
  }

  selectHasCartContract(): void {
    this.marketingFacade.selectHasCartContract().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => this.hasCartContract = value);
  }

  /**
   * Method to select Pricing Group toggle
   * @private
   */
  private selectPricingGroupToggle(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_PRICING_GROUP).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.pricingGroupToggle = value;
    });
  }

  selectFeaturedProducts(): void {
    this.featuredProductsFacade.getFeaturedProducts(this.featuredProductsKey)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data) => {
          const featuredProductsResponse: IFeatureProducts = data;
          const featuredProducts: IFeatureProductsData[] = featuredProductsResponse.data ?? [];
          const featuredProductsCount: number = featuredProducts.length;
          const featuredProductsIncluded: Array<IProductImage | IFeatureProductPrices | IProductLabel> | undefined =
                  featuredProductsResponse.included;

          if (featuredProductsIncluded) {
            this.labels = featuredProductsIncluded.filter(item => item.type === 'product-labels') as IProductLabel[];
          }

          this.isLoaded = true;
          this.hasProducts = featuredProducts.length > 0;
          this.slideConfigHorizontal = this.getHorizontalConfig(featuredProductsCount);
          this.slideConfigVertical = this.getVerticalConfig(featuredProductsCount);
          this.featuredProductsWithPricesAndImages = featuredProducts.map(productData => {
            return FeaturedProductsUtils.getDisplayableProduct(productData, featuredProductsIncluded ?? []);
          });
        },
      );
  }

  selectItemsInQueueToCart(): void {
    this.marketingFacade.selectItemsInQueueToCart()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(state => {
        this.itemsInQueueToCart = state;
      });
  }

  selectLayoutOption(option: string): void {
    this.currentLayoutType = option;
  }

  hasGuestPrice(product: IFeatureProductWithImage): boolean {
    return this.priceUtils.hasGuestPrice(product.attributes.prices);
  }

  getGuestPrice(product: IFeatureProductWithImage): number | undefined {
    return this.priceUtils.getPriceValue(this.priceUtils.getGuestPrice(product.attributes.prices));
  }

  /**
   * Add specific product to cart.
   * If equipment is required and not selected, open modal to select equipment.
   * If cart has contract, open modal to confirm adding item to cart.
   * Otherwise, add item directly to cart.
   *
   * @param product
   */
  addToCart(product: IFeatureProductWithImage): void {
    if (this.isItemInProgress(product.attributes?.sku) || this.loadingCartDataInProgress) {
      return;
    }

    const systemDetails: ISystemDetails | undefined = this.currentCart.attributes?.systemDetails;

    const isEquipmentRequired: boolean = CartUtils.determineIfEquipmentIsRequired(
      this.isMyInstalledBaseFlowEnabled,
      this.isNonEquipmentPurchaseEnabled,
      this.supportedCategoriesForNonEquipmentPurchase,
      product.attributes?.webshopProductCategory as string,
    );

    if (!this.isPredefinedEquipmentFlowEnabled && isEquipmentRequired) {
      if (!systemDetails?.siemensEquipmentId) {
        this.productPricingGroup = product.attributes?.pricingGroup as string;
        this.showModalSelectEquipment = true;
        return;
      }
    }

    if (this.hasCartContract) {
      this.itemToAdd = [{
        data: {
          sku: product.attributes?.sku,
          name: product.attributes?.name || product.attributes?.sku,
        },
        isConcrete: false,
        quantity: 1,
      }];
      this.addItemToCartModalActive = true;
      return;
    }

    const productData: {sku: string, name: string, sap_product_sparepart?: string} = {
      sku: product.attributes?.sku,
      name: product.attributes?.name || product.attributes?.sku,
      sap_product_sparepart: product.attributes?.sap_product_sparepart,
    };

    this.marketingFacade.addProductToCart(productData, false);
    this.analyticsService.setProducts(productData);
    this.analyticsService.trackCart('cart.add');
  }

  isAddToCartBtnDisabled(): boolean {
    return this.loadingCartDataInProgress || (!this.isSapIntegration && this.isSapIntegrationRequired);
  }

  isItemInProgress(sku: string): boolean {
    return this.itemsInQueueToCart.includes(sku);
  }

  getHorizontalConfig(productCount: number): any {
    return {
      slidesToShow: 3,
      slidesToScroll: Math.min(productCount, 3),
      dots: true,
      arrows: true,
      responsive: [
        {
          breakpoint: 992,
          settings: {
            slidesToShow: 2,
            slidesToScroll: Math.min(productCount, 2),
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 2,
            slidesToScroll: Math.min(productCount, 2),
            arrows: false,
          },
        },
        {
          breakpoint: 576,
          settings: {
            slidesToShow: 1,
            slidesToScroll: Math.min(productCount, 1),
            arrows: false,
          },
        },
      ],
    };
  }

  getVerticalConfig(productCount: number): any {
    return {
      slidesToShow: Math.min(productCount, 10),
      slidesToScroll: Math.min(productCount, 10),
      dots: true,
      arrows: false,
      vertical: true,
      verticalSwiping: true,
    };
  }

  dispatchActionToGetWishlists(): void {
    this.wishlistFacade.dispatchUsersWishlistRetrieve();
  }

  getWishlistsFromStore(): void {
    this.wishlistFacade.selectUsersWishlists().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(wishlists => this.wishlists = wishlists);
  }

  wishlistCreated(_event: any): void {
    this.wishlistFacade.isNewWishlistCreated()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => {
        this.addNewModalActive = !result;
      });
  }

  /**
   * Method to select if customer is business partner
   */
  private selectIsCustomerBusinessPartner(): void {
    this.customerFacade.isBusinessPartner().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isBusinessPartner => {
      this.isBusinessPartner = isBusinessPartner;
    });
  }

  /**
   * Returns true if product is available for online purchase
   *  user has to be non-business partner
   *  FL has to be selected
   *  product is available for online purchase
   *    if product is consumable and system details is not isSparePartsAvailable
   *    or system details is isSparePartsAvailable
   *
   * @return {boolean}
   */
  checkIfProductIsAvailableForOnlinePurchase(product: IFeatureProductWithImage): boolean {
    if (!this.isBusinessPartner
      && this.currentCart?.attributes?.systemDetails?.siemensEquipmentId
      && this._isPricingGroupAvailableForCurrentStore()) {
      return (
        product.attributes.pricingGroup === this.pricingGroupForConsumables
        && !this.currentCart?.attributes?.systemDetails.isSparePartsAvailable
      ) || this.currentCart?.attributes?.systemDetails.isSparePartsAvailable;
    } else {
      return true;
    }
  }

  /**
   * In case the pricingGroupForConsumables is equal to its own translate key,
   * it means the pricing group is not set for current store
   *
   * @returns {boolean}
   * @private
   */
  private _isPricingGroupAvailableForCurrentStore(): boolean {
    return this.pricingGroupForConsumables !== CONFIG_PRICING_GROUP_FOR_CONSUMABLES;
  }

  /**
   * Method for retrieving IsMyInstalledBaseFlowEnabled feature toggle value
   * @private
   */
  private selectIsMyInstalledBaseFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.MY_INSTALLED_BASE_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isMyInstalledBaseFlowEnabled => {
      this.isMyInstalledBaseFlowEnabled = isMyInstalledBaseFlowEnabled;
    });
  }

  /**
   * Method for retrieving IsPredefinedEquipmentFlowEnabled feature toggle value
   * @private
   */
  private _selectIsPredefinedEquipmentFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isPredefinedEquipmentFlowEnabled => {
      this.isPredefinedEquipmentFlowEnabled = isPredefinedEquipmentFlowEnabled;
    });
  }

  /**
   * Method for retrieving IsNonEquipmentPurchaseEnabled feature toggle value
   * @private
   */
  private _isNonEquipmentPurchaseEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.NON_EQUIPMENT_PURCHASE).pipe(
      tap((isEnabled: boolean) => {
        if (isEnabled) {
          this.configurationFacade.getNonEquipmentPurchaseSupportedCategories$().pipe(
            take(1),
          ).subscribe((categories: string[]) => {
            this.supportedCategoriesForNonEquipmentPurchase = categories;
          });
        }
        this.isNonEquipmentPurchaseEnabled = isEnabled;
      }),
      take(1),
    ).subscribe();
  }
}
